/**
 * Where she is in the journey.
 *
 * Extracted from `CycleDetailScreen` so the rule is testable: that screen cannot
 * be imported by a test, and this pointer decides which tab the treatment hub
 * opens on, which step the roadmap marks active, and — once it runs out of
 * steps — whether the journey reads as finished at all.
 *
 * The rule it replaces returned the first step that was not completed or
 * skipped, scanning from the start of the journey and never looking at a date.
 * One unfilled step therefore pinned the pointer to itself for good: filling
 * every later step, even with earlier dates, moved nothing, and resolving the
 * last step of the cycle threw her back to a gap near the beginning.
 */

type PointerStep = { id: string };
type PointerProgress = { stepId: string; status?: string };

/**
 * A step is resolved when she has said what happened to it — completed, or
 * skipped. Absent is not resolved: a step with no progress row at all is one she
 * has not answered.
 */
function isResolved(
  stepId: string,
  progress: readonly PointerProgress[],
): boolean {
  const status = progress.find((entry) => entry.stepId === stepId)?.status;
  return status === "completed" || status === "skipped";
}

/**
 * The step she is on, or null when the journey has none left.
 *
 * Never behind the furthest step she has resolved. Gaps before that point stay
 * reachable through the roadmap — they are hers to fill whenever she wants — but
 * they stop claiming to be where she is, which is the claim that made the hub
 * jump backwards under her.
 *
 * Deliberately not date-aware. Dates are what the patient and the clinic
 * recorded about *events*; how far she has got through the journey is a
 * different question, and answering it from dates would put her back in the
 * stimulation phase every time she entered a historical value.
 */
export function resolveJourneyPointer(
  steps: readonly PointerStep[],
  progress: readonly PointerProgress[],
): string | null {
  let lastResolved = -1;
  for (let index = 0; index < steps.length; index += 1) {
    if (isResolved(steps[index]!.id, progress)) lastResolved = index;
  }
  return steps[lastResolved + 1]?.id ?? null;
}

/**
 * A step she has moved past without filling in.
 *
 * The gaps `resolveJourneyPointer` deliberately steps over. They are not
 * upcoming — she is already beyond them — and not skipped either, because she
 * never said so; they are simply unanswered, and still hers to answer.
 *
 * Naming them matters because the surfaces had no word for it. The treatment
 * hub badged such a step "Planned", beside an estimated date range, on a cycle
 * whose *result* was already recorded: it announced as future a step that was
 * months behind her. "Planned" is a promise about what is coming, and this is
 * the opposite of that.
 *
 * Resolved from the collection, not from one row: what makes a gap a gap is
 * that something *after* it has been answered.
 */
export function isStepPassedUnfilled(
  stepId: string,
  steps: readonly PointerStep[],
  progress: readonly PointerProgress[],
): boolean {
  const index = steps.findIndex((step) => step.id === stepId);
  if (index === -1 || isResolved(stepId, progress)) return false;
  return steps.some((step, at) => at > index && isResolved(step.id, progress));
}
